import React, { FunctionComponent, useContext, useMemo, useRef } from 'react'
import { defineMessages, FormattedMessage, useIntl } from 'react-intl'
import { NativeScrollEvent, NativeSyntheticEvent, ScrollView } from 'react-native'

import { isEmpty, noop } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import {
  BENEFITS_SECTION_ID,
  careNavigatorButtonLabel,
  CostMessagingModalContent,
  CostMessagingModalCoverageInfo,
  INTEGRATED_BENEFITS_SECTION_TYPE,
  SessionCountByProgram,
  SessionLimitByProgram,
} from '@lyrahealth-inc/shared-app-logic'

import { ContentSectionsWithNav, getAuroraAwareBackgroundColor, useDebouncedCallback } from '../..'
import { BodyText, Link } from '../../atoms'
import { BackButtonV2Type as BackButtonType, BackButtonV2 } from '../../atoms/backButton/BackButtonV2'
import { Headline, Size as HeadlineSize } from '../../atoms/headline/Headline'
import { IS_WEB } from '../../constants'
import { AppContext } from '../../context'
import { FeaturedBenefitsSection } from '../../molecules'
import { CoverageMessagingLink } from '../../molecules/coverageMessagingLink/CoverageMessagingLink'
import { IntegratedBenefitsItemProps } from '../../molecules/integratedBenefitsItem/IntegratedBenefitsItem'
import { ContentSectionData } from '../../organisms/contentSectionsWithNav/ContentSectionsWithNav'
import { IntegratedBenefitsLabeledItemGroup } from '../../organisms/integratedBenefitsLabeledItemGroup/IntegratedBenefitsLabeledItemGroup'
import {
  OnsiteProviderInfoModal,
  type OnsiteProviderInfoModalProps,
} from '../../organisms/onsiteProviderInfoModal/OnsiteProviderInfoModal'
import { ThemeType } from '../../utils'
import { tID } from '../../utils/utils'

export interface IntegratedBenefitsPageProps {
  recommendedBenefitsItems?: IntegratedBenefitsItemProps[]
  moreBenefitsItems?: IntegratedBenefitsItemProps[]
  lyraBenefitsItems?: IntegratedBenefitsItemProps[]
  additionalCareBenefitsItems?: IntegratedBenefitsItemProps[]
  featuredBenefits?: IntegratedBenefitsItemProps[]
  onPressBackButton?: () => any
  onCareNavigatorPress: () => void
  coverageModalTitleText?: React.ReactNode
  coverageModalContents?: CostMessagingModalContent[]
  costMessagingModalCoverageInfo?: CostMessagingModalCoverageInfo
  isCostMessagingEnabled: boolean
  trackCoverageModalPress: (isOpen: boolean) => void
  widerBenefitsItemTitleContainer?: boolean
  customerName?: string
  numSessions?: string
  numberOfSessionByProgram?: SessionLimitByProgram
  displayVisitsPerIssuePerYear?: number
  displayVisitsPerIssuePerYearByProgram?: SessionCountByProgram
  onsiteModalProps?: Partial<OnsiteProviderInfoModalProps>
  trackContentSectionNavEvent: (benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE) => void
  footerNode?: React.ReactNode
}

const contentContainerStyle = (isMobileSized: boolean) => {
  return {
    paddingHorizontal: isMobileSized ? 16 : 32,
    maxWidth: '1444px',
    width: '100%',
  }
}
const BackgroundContainer = styled.ScrollView<{ theme: ThemeType }>(
  ({ theme: { colors, spacing, breakpoints, isAuroraTheme } }) => ({
    backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary),
    paddingTop: breakpoints.isMobileSized ? 0 : spacing['32px'],
    ...(IS_WEB && { overflow: 'unset', overflowY: 'unset', overflowX: 'unset' }),
    width: '100%',
  }),
)

const HeadlineContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginTop: theme.spacing['8px'],
  marginBottom: theme.spacing['24px'],
  flexShrink: 1,
  ...contentContainerStyle(theme.breakpoints.isMobileSized),
}))

const BackButtonContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  ...(theme.breakpoints.isMinWidthDesktop
    ? {
        position: 'absolute',
        left: '-48px',
      }
    : {
        paddingLeft: theme.spacing['32px'],
        marginBottom: theme.spacing['8px'],
      }),
}))

const CareNavBottomSection = styled(BodyText)<{ theme: ThemeType }>(({ theme }) => ({
  marginVertical: '60px',
  ...contentContainerStyle(theme.breakpoints.isMobileSized),
}))

export const benefitsHeaderText = (
  <FormattedMessage
    defaultMessage='Explore your benefits'
    description='Suggestion for the user to view their benefits on this page'
  />
)

export const benefitsSectionLabels = defineMessages({
  ADDITIONAL_CARE_OPTIONS: {
    defaultMessage: 'Additional care options',
    description: 'Label for a group of additional benefits offered through Lyra',
  },
  RECOMMENDED_FOR_YOU: {
    defaultMessage: 'Recommended for you',
    description: 'Label for a group of benefits recommended to the user',
  },
  MORE_BENEFITS: {
    defaultMessage: 'More benefits',
    description: 'Label for a group other benefits offered',
  },
  FEATURED: {
    defaultMessage: 'Featured',
    description: 'Label for a group of popular benefits recommended to the user',
  },
  CORE_BENEFITS: {
    defaultMessage: 'Core benefits',
    description: 'Label for a group of core benefits offered',
  },
  SPECIALIZED_CARE: {
    defaultMessage: 'Specialized care',
    description: 'Label for a group of specialized care benefits offered',
  },
  CURATED_FOR_YOU: {
    defaultMessage: 'Curated for you',
    description: 'Label for a group of benefits curated for the user',
  },
  MORE_BENEFITS_NEW: {
    defaultMessage: 'More benefits from {customerName}',
    description: `Label for a group of specific benefits offered by the user's employer`,
  },
  LYRA_BENEFITS: {
    defaultMessage: 'Lyra benefits',
    description: 'Label for single lyra benefits',
  },
})

const DEFAULT_NUM_FEATURED_BENEFITS = 3

export const IntegratedBenefitsPage: FunctionComponent<IntegratedBenefitsPageProps> = ({
  recommendedBenefitsItems,
  lyraBenefitsItems,
  moreBenefitsItems,
  additionalCareBenefitsItems,
  featuredBenefits = [],
  onPressBackButton = noop,
  onCareNavigatorPress,
  coverageModalTitleText,
  coverageModalContents = [],
  costMessagingModalCoverageInfo = undefined,
  isCostMessagingEnabled,
  trackCoverageModalPress,
  widerBenefitsItemTitleContainer = false,
  customerName,
  numSessions,
  numberOfSessionByProgram,
  displayVisitsPerIssuePerYear,
  displayVisitsPerIssuePerYearByProgram,
  onsiteModalProps,
  trackContentSectionNavEvent,
  footerNode,
}) => {
  const {
    breakpoints: { isMobileSized, isMinWidthLaptop },
    colors,
    isAuroraTheme,
  } = useTheme() as ThemeType
  const { isNewBenefitsPageDesignEnabled } = useContext(AppContext)
  const { formatMessage } = useIntl()
  const scrollViewRef = useRef<ScrollView>(null)
  const debouncedHandleScroll = useDebouncedCallback((scrollY: number) => {
    ;(scrollViewRef.current as any)?.handleContentScroll(scrollY)
  }, 200)

  const benefitsSections = useMemo(
    () => [
      {
        id: BENEFITS_SECTION_ID.FEATURED,
        sectionName: formatMessage(benefitsSectionLabels.FEATURED),
        benefitsItems: featuredBenefits,
        isFeatured: true,
        isEnabled:
          isNewBenefitsPageDesignEnabled &&
          featuredBenefits &&
          featuredBenefits.length === DEFAULT_NUM_FEATURED_BENEFITS,
        benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE.FEATURED,
      },
      {
        id: BENEFITS_SECTION_ID.CORE_BENEFITS,
        sectionName: formatMessage(
          isNewBenefitsPageDesignEnabled ? benefitsSectionLabels.CORE_BENEFITS : benefitsSectionLabels.LYRA_BENEFITS,
        ),
        benefitsItems: lyraBenefitsItems,
        shouldShowCoverageLink: true,
        isEnabled: true,
        benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE.CORE_BENEFITS,
      },
      {
        id: BENEFITS_SECTION_ID.SPECIALIZED_CARE,
        sectionName: formatMessage(
          isNewBenefitsPageDesignEnabled
            ? benefitsSectionLabels.SPECIALIZED_CARE
            : benefitsSectionLabels.ADDITIONAL_CARE_OPTIONS,
        ),
        benefitsItems: additionalCareBenefitsItems,
        isEnabled: true,
        benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE.SPECIALIZED_CARE,
      },
      {
        id: BENEFITS_SECTION_ID.CURATED_FOR_YOU,
        sectionName: formatMessage(
          isNewBenefitsPageDesignEnabled
            ? benefitsSectionLabels.CURATED_FOR_YOU
            : benefitsSectionLabels.RECOMMENDED_FOR_YOU,
        ),
        benefitsItems: recommendedBenefitsItems,
        shouldShowRecommendedBenefitsTooltip: true,
        isEnabled: true,
        benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE.CURATED_FOR_YOU,
      },
      {
        id: BENEFITS_SECTION_ID.MORE_BENEFITS,
        sectionName: formatMessage(
          isNewBenefitsPageDesignEnabled
            ? benefitsSectionLabels.MORE_BENEFITS_NEW
            : benefitsSectionLabels.MORE_BENEFITS,
          { customerName },
        ),
        benefitsItems: moreBenefitsItems,
        isEnabled: true,
        benefitSectionType: INTEGRATED_BENEFITS_SECTION_TYPE.MORE_BENEFITS_FROM_COMPANY,
      },
    ],
    [
      featuredBenefits,
      lyraBenefitsItems,
      additionalCareBenefitsItems,
      recommendedBenefitsItems,
      moreBenefitsItems,
      isNewBenefitsPageDesignEnabled,
      formatMessage,
      customerName,
    ],
  )

  const benefitsSectionsDisplayed = useMemo(() => {
    const filteredSections = benefitsSections.filter((section) => !isEmpty(section.benefitsItems) && section.isEnabled)

    if (!filteredSections.length) return filteredSections

    const firstSection = filteredSections[0]
    // if only core benefits section, update section name
    if (filteredSections.length === 1 && firstSection.id === BENEFITS_SECTION_ID.CORE_BENEFITS) {
      return [
        {
          ...firstSection,
          sectionName: formatMessage(benefitsSectionLabels.LYRA_BENEFITS),
        },
      ]
    }

    // if only featured + core sections, remove featured if core section has ≤5 items
    const shouldRemoveFeaturedSection =
      filteredSections.length === 2 &&
      firstSection.id === BENEFITS_SECTION_ID.FEATURED &&
      filteredSections[1].id === BENEFITS_SECTION_ID.CORE_BENEFITS &&
      (filteredSections[1].benefitsItems?.length ?? 0) <= 5
    return shouldRemoveFeaturedSection ? filteredSections.slice(1) : filteredSections
  }, [benefitsSections, formatMessage])

  const getCoverageLink = () => {
    return (
      <CoverageMessagingLink
        coverageModalTitleText={coverageModalTitleText}
        coverageModalContents={coverageModalContents}
        costMessagingModalCoverageInfo={costMessagingModalCoverageInfo}
        isCostMessagingEnabled={isCostMessagingEnabled}
        trackCoverageModalPress={trackCoverageModalPress}
        customerName={customerName}
        numSessions={numSessions}
        numberOfSessionByProgram={numberOfSessionByProgram}
        displayVisitsPerIssuePerYear={displayVisitsPerIssuePerYear}
        displayVisitsPerIssuePerYearByProgram={displayVisitsPerIssuePerYearByProgram}
      />
    )
  }

  const sections: ContentSectionData[] = benefitsSectionsDisplayed.map((sectionData) => ({
    id: sectionData.id,
    name: sectionData.sectionName,
    content: sectionData.isFeatured ? (
      <FeaturedBenefitsSection benefits={sectionData.benefitsItems} />
    ) : (
      <IntegratedBenefitsLabeledItemGroup
        label={sectionData.sectionName}
        items={sectionData.benefitsItems ?? []}
        widerBenefitsItemTitleContainer={widerBenefitsItemTitleContainer}
        coverageMessageLinkNode={sectionData.shouldShowCoverageLink ? getCoverageLink() : undefined}
        showRecommendedBenefitsTooltip={sectionData.shouldShowRecommendedBenefitsTooltip}
      />
    ),
    benefitSectionType: sectionData.benefitSectionType,
  }))

  const { NavigationLinks, ContentSections } = ContentSectionsWithNav({
    sections,
    isEnabled: isNewBenefitsPageDesignEnabled && sections && sections.length > 1,
    testId: 'IntegratedBenefitsPage',
    scrollViewRef,
    contentContainerStyle: contentContainerStyle(isMobileSized),
    backgroundColor: getAuroraAwareBackgroundColor(isAuroraTheme, colors.backgroundSecondary),
    trackContentSectionNavEvent: trackContentSectionNavEvent,
  })

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const scrollY = event.nativeEvent.contentOffset.y
    debouncedHandleScroll(scrollY)
  }

  return (
    <BackgroundContainer
      testID={tID('IntegratedBenefitsPage')}
      stickyHeaderIndices={isNewBenefitsPageDesignEnabled ? [!IS_WEB ? 0 : 2] : undefined}
      ref={scrollViewRef}
      onScroll={handleScroll}
      contentContainerStyle={IS_WEB && isMinWidthLaptop ? { alignSelf: 'center' } : undefined}
    >
      {!isMobileSized && (
        <BackButtonContainer testID={tID('IntegratedBenefitsPage-back_button')}>
          <BackButtonV2 onPress={onPressBackButton} backButtonType={BackButtonType.OATMEAL} />
        </BackButtonContainer>
      )}
      {!isMobileSized && (
        <HeadlineContainer>
          <Headline
            level='1'
            color={colors.textPrimary}
            size={HeadlineSize.SMALL}
            textAlign='left'
            text={benefitsHeaderText}
          />
        </HeadlineContainer>
      )}

      {NavigationLinks}
      {ContentSections}

      <CareNavBottomSection
        text={
          <FormattedMessage
            {...careNavigatorButtonLabel}
            values={{
              link: (text: string) => (
                <Link
                  underline={true}
                  text={text}
                  onPress={onCareNavigatorPress}
                  testID={tID('IBPage-contactCareNavigator')}
                />
              ),
            }}
          />
        }
      />

      {footerNode}

      <OnsiteProviderInfoModal {...onsiteModalProps} />
    </BackgroundContainer>
  )
}
